import usePermissions from "@/hooks/use-permissions.hook.ts";
import { HomeLayoutEditor } from "@/pages/home/components/HomeLayoutEditor.tsx";
import { isHomeWidgetAvailable } from "@/pages/home/widgets/home-widget.registry.tsx";
import {
  useHomeLayout,
  useHomeLayoutReset,
  useHomeLayoutSave,
} from "@/queries/use-employee-settings.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { ApiEmployeeHomeLayoutWidget } from "@/types/employee-settings.types.ts";
import { Button, Modal, ModalProps, Skeleton, Stack, useModals } from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export type HomeCustomizeModalProps = ModalProps;

export function HomeCustomizeModal({ ...props }: HomeCustomizeModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { canIEdit, isFeatureEnabled } = usePermissions();

  const { data, isLoading } = useHomeLayout();
  const saveMutation = useHomeLayoutSave();
  const resetMutation = useHomeLayoutReset();

  const [draft, setDraft] = useState<ApiEmployeeHomeLayoutWidget[]>([]);
  const [seededSetting, setSeededSetting] = useState<typeof data>(undefined);
  if (data && data !== seededSetting) {
    setSeededSetting(data);
    setDraft(data.widgets);
  }

  const handleSave = async () => {
    const widgets = draft
      .filter((widget) => isHomeWidgetAvailable(widget.id, { canIEdit, isFeatureEnabled }))
      .map(({ id, hidden }) => ({ id, hidden }));
    await saveMutation.mutateAsync({ widgets });
    analyticsService.trackEvent(analyticEvents.home.layoutSaved);
    close(props.id);
  };

  const handleReset = async () => {
    await resetMutation.mutateAsync();
  };

  return (
    <Modal
      {...props}
      variant={"side"}
      title={t("core:customize_home_screen")}
      layout={"stretch"}
      withCloser
      closeByEscEnabled
      actions={
        <Stack gap={"sm"} direction={"vertical"}>
          <Button
            variant="primary"
            size={"lg"}
            onClick={handleSave}
            isLoading={saveMutation.isPending}
          >
            {t("common:save")}
          </Button>
          <Button
            variant={"secondary"}
            size={"md"}
            onClick={handleReset}
            isLoading={resetMutation.isPending}
          >
            {t("common:reset")}
          </Button>
        </Stack>
      }
    >
      {isLoading ? (
        <Skeleton className={"w-full h-64"} />
      ) : (
        <HomeLayoutEditor widgets={draft} onChange={setDraft} />
      )}
    </Modal>
  );
}
